// ===== USE SHEET DOWNLOAD HOOK =====
// Hook para gerenciar download de partituras
// Extraido de SheetDetailModal para melhor testabilidade

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '@constants/api';
import { API } from '@services/api';
import { Storage } from '@services/storage';

/**
 * Salva um blob como arquivo para download
 * Compativel com navegadores modernos e IE/Edge legado
 */
const saveBlob = (blob, filename) => {
  // Metodo 1: msSaveBlob para IE/Edge legado
  if (window.navigator.msSaveBlob) {
    window.navigator.msSaveBlob(blob, filename);
    return;
  }

  // Metodo 2: Criar link com blob URL
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  // Importante: adicionar ao DOM antes de clicar
  document.body.appendChild(link);

  // Usar setTimeout para garantir que o DOM foi atualizado
  setTimeout(() => {
    link.click();
    // Limpar apos um tempo maior
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  }, 0);
};

/**
 * Normaliza nome de instrumento para comparacao
 * Converte variantes como "Saxofone Alto" -> "sax alto"
 */
const normalizeInstrumento = (nome) => {
  let normalized = nome.toLowerCase()
    .replace(/\./g, '') // Remove pontos: "Sax." -> "Sax"
    .replace(/saxofone/g, 'sax') // Normaliza Saxofone -> Sax
    .replace(/clarineta/g, 'clarinete') // Variante
    .replace(/^(euphonium|euf[oô]nio)\b/g, 'bombardino')
    .replace(/b♭/g, 'bb')
    .replace(/\bsi\s*b(?:emol)?\b/g, 'bb')
    .replace(/\bd[oó]\b/g, 'c')
    .replace(/\s+/g, ' ') // Colapsa espacos
    .trim();

  // No acervo, Bombardino sem tonalidade sempre significa Bombardino C.
  if (/^bombardino(?:\s|$)/.test(normalized) && !/\s+(?:bb|eb|c|tc|bc)(?=\s|\d|$)/.test(normalized)) {
    normalized = normalized.replace(/^bombardino\b/, 'bombardino c');
  }

  return normalized;
};

const GRADE_INSTRUMENT_ALIASES = new Set([
  'grade',
  'score',
  'conductor',
  'full score',
  'partitura',
  'maestro',
  'regente'
]);

const isGradeInstrument = (instrumento) => {
  if (typeof instrumento !== 'string') return false;
  return GRADE_INSTRUMENT_ALIASES.has(normalizeInstrumento(instrumento));
};

export const getParteTrackingEventType = (parteOrAction, actionOrInstrumento = 'download') => {
  const action = typeof parteOrAction === 'string' ? parteOrAction : actionOrInstrumento;
  const instrumento = typeof parteOrAction === 'string'
    ? actionOrInstrumento
    : parteOrAction?.instrumento;
  const isGrade = isGradeInstrument(instrumento);
  if (action === 'view') {
    return isGrade ? 'pdf_visualizado_grade' : 'pdf_visualizado_parte';
  }
  return isGrade ? 'download_grade' : 'download_parte';
};

/**
 * Extrai tonalidade de um nome de instrumento normalizado
 * Ex: "trompa f" -> "f", "trompete bb 1" -> "bb", "clarinete" -> null
 */
const extractTonalidade = (nome) => {
  const match = nome.match(/\s+(bb|eb|tc|bc|f|c)(?=\s|\d|$)/i);
  return match ? match[1].toLowerCase() : null;
};

/**
 * Encontra partes correspondentes ao instrumento
 * Considera variacoes como "Trompete Bb 1", "Trompete Bb 2"
 * E normaliza nomes: "Saxofone Soprano" -> "Sax Soprano"
 * IMPORTANTE: Nao mistura tonalidades (Trompa F nao baixa Trompa Eb)
 */
export const findPartesCorrespondentes = (instrumento, partes) => {
  if (!instrumento || partes.length === 0) return [];

  const instrNorm = normalizeInstrumento(instrumento);
  const instrTonalidade = extractTonalidade(instrNorm);
  const instrBase = instrNorm.replace(/\s*(bb|eb|f|c)?\s*\d*$/i, '').trim();

  return partes.filter(p => {
    const parteNorm = normalizeInstrumento(p.instrumento);
    const parteTonalidade = extractTonalidade(parteNorm);
    const parteBase = parteNorm.replace(/\s*(bb|eb|f|c)?\s*\d*$/i, '').trim();

    // Se ambos tem tonalidade e sao diferentes, nao combina
    // Ex: Trompa F nao baixa Trompa Eb
    if (instrTonalidade && parteTonalidade && instrTonalidade !== parteTonalidade) {
      return false;
    }

    return parteNorm === instrNorm ||
      parteNorm.startsWith(instrNorm) ||
      parteBase === instrBase ||
      instrNorm.startsWith(parteBase);
  });
};

/**
 * Encontra parte exata pelo nome do instrumento
 */
export const findParteExata = (instrumento, partes) => {
  if (typeof instrumento !== 'string' || !Array.isArray(partes)) return undefined;
  return partes.find(
    p => typeof p.instrumento === 'string' && p.instrumento.toLowerCase() === instrumento.toLowerCase()
  );
};

/**
 * Hook para gerenciar downloads de partituras
 * @param {Object} options - Opcoes do hook
 * @param {Function} options.showToast - Funcao para exibir toast messages
 * @param {Object} options.selectedSheet - Partitura selecionada
 * @param {Array} options.partes - Lista de partes disponiveis
 */
export const useSheetDownload = ({ showToast, selectedSheet, partes = [] }) => {
  const [downloading, setDownloading] = useState(false);
  const [confirmInstrument, setConfirmInstrument] = useState(null);
  const [selectedParte, setSelectedParte] = useState(null);
  const [showPartePicker, setShowPartePicker] = useState(false);
  const [partesDisponiveis, setPartesDisponiveis] = useState([]);

  // Estado para o visualizador de PDF embutido (mobile)
  const [pdfViewer, setPdfViewer] = useState({
    isOpen: false,
    url: null,
    title: '',
    instrument: ''
  });

  /**
   * Download direto de uma parte especifica
   */
  const downloadParteDireta = useCallback(async (parte) => {
    if (downloading || !selectedSheet) return;
    setDownloading(true);

    showToast(`Preparando "${selectedSheet.title}" - ${parte.instrumento}...`);

    try {
      const response = await fetch(`${API_BASE_URL}/api/download/parte/${parte.id}`, {
        headers: { 'Authorization': `Bearer ${Storage.get('authToken')}` }
      });

      if (response.ok) {
        // Extrair nome do arquivo do header Content-Disposition se disponivel
        let filename = `${selectedSheet.title} - ${parte.instrumento}.pdf`;
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="(.+)"/);
          if (match) filename = match[1];
        }

        const blob = await response.blob();
        // Criar blob com tipo explicito para PDF
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });

        API.trackEvent({
          tipo: getParteTrackingEventType(parte),
          origem: 'detalhe_partitura',
          partitura_id: selectedSheet.id,
          parte_id: parte.id,
          metadata: { instrumento: parte.instrumento }
        });

        // Usar funcao de save dedicada
        saveBlob(pdfBlob, filename);

        showToast('Download iniciado!');
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.error || 'Erro ao baixar arquivo', 'error');
      }
    } catch (e) {
      console.error('Erro no download:', e);
      showToast('Erro ao baixar arquivo', 'error');
    }

    setDownloading(false);
    setShowPartePicker(false);
    setConfirmInstrument(null);
  }, [downloading, selectedSheet, showToast]);

  /**
   * Download do arquivo completo da partitura (fallback)
   */
  const downloadCompleto = useCallback(async () => {
    if (downloading || !selectedSheet) return;
    setDownloading(true);

    showToast(`Preparando "${selectedSheet.title}"...`);

    try {
      const response = await fetch(`${API_BASE_URL}/api/download/${selectedSheet.id}`, {
        headers: { 'Authorization': `Bearer ${Storage.get('authToken')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        API.trackEvent({
          tipo: 'download_grade',
          origem: 'detalhe_partitura',
          partitura_id: selectedSheet.id
        });
        saveBlob(pdfBlob, `${selectedSheet.title}.pdf`);
        showToast('Iniciando download...');
      } else {
        showToast('Erro ao baixar arquivo', 'error');
      }
    } catch (e) {
      console.error('Erro no download:', e);
      showToast('Erro ao baixar arquivo', 'error');
    }

    setDownloading(false);
  }, [downloading, selectedSheet, showToast]);

  /**
   * Seleciona um instrumento e decide o fluxo de download
   */
  const handleSelectInstrument = useCallback((instrument) => {
    const correspondentes = findPartesCorrespondentes(instrument, partes);

    if (correspondentes.length === 0) {
      // Nenhuma parte encontrada - vai para confirmacao
      setConfirmInstrument(instrument);
    } else if (correspondentes.length === 1) {
      // Apenas uma parte - download direto
      downloadParteDireta(correspondentes[0]);
    } else {
      // Multiplas partes - mostrar picker
      setPartesDisponiveis(correspondentes);
      setShowPartePicker(true);
      setConfirmInstrument(instrument);
    }
  }, [partes, downloadParteDireta]);

  /**
   * Seleciona uma parte especifica pelo nome exato
   */
  const handleSelectParteEspecifica = useCallback((instrumento) => {
    const parte = findParteExata(instrumento, partes);
    if (parte) {
      setConfirmInstrument(instrumento);
      setSelectedParte(parte);
    } else {
      showToast('Parte não encontrada', 'error');
    }
  }, [partes, showToast]);

  /**
   * Confirma e executa o download
   */
  const handleConfirmDownload = useCallback(async () => {
    if (selectedParte) {
      await downloadParteDireta(selectedParte);
      setConfirmInstrument(null);
      setSelectedParte(null);
      return;
    }

    const correspondentes = findPartesCorrespondentes(confirmInstrument, partes);

    if (correspondentes.length > 0) {
      await downloadParteDireta(correspondentes[0]);
    } else {
      await downloadCompleto();
    }

    setConfirmInstrument(null);
  }, [selectedParte, confirmInstrument, partes, downloadParteDireta, downloadCompleto]);

  /**
   * Cancela o fluxo de download
   */
  const handleCancelDownload = useCallback(() => {
    setConfirmInstrument(null);
    setSelectedParte(null);
    setShowPartePicker(false);
    setPartesDisponiveis([]);
  }, []);

  /**
   * Fecha o picker de partes
   */
  const closePartePicker = useCallback(() => {
    setShowPartePicker(false);
    setConfirmInstrument(null);
  }, []);

  /**
   * Imprime uma parte especifica
   */
  const printParte = useCallback(async (parte) => {
    if (downloading || !selectedSheet) return;
    setDownloading(true);

    showToast(`Preparando impressão "${selectedSheet.title}" - ${parte.instrumento}...`);

    try {
      const response = await fetch(`${API_BASE_URL}/api/download/parte/${parte.id}`, {
        headers: { 'Authorization': `Bearer ${Storage.get('authToken')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
        showToast('Abrindo impressão...');
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.error || 'Erro ao preparar impressão', 'error');
      }
    } catch (e) {
      console.error('Erro na impressão:', e);
      showToast('Erro ao preparar impressão', 'error');
    }

    setDownloading(false);
  }, [downloading, selectedSheet, showToast]);

  /**
   * Inicia fluxo de impressão para um instrumento
   */
  const handlePrintInstrument = useCallback((instrument) => {
    // Tenta correspondencia exata primeiro (resolve bug Clarinete Bb 1 vs 2)
    const exata = findParteExata(instrument, partes);
    if (exata) {
      printParte(exata);
      return;
    }

    const correspondentes = findPartesCorrespondentes(instrument, partes);

    if (correspondentes.length === 0) {
      showToast('Parte não encontrada para impressão', 'error');
    } else {
      printParte(correspondentes[0]);
    }
  }, [partes, printParte, showToast]);

  /**
   * Visualiza uma parte especifica (abre no modal embutido)
   */
  const viewParte = useCallback(async (parte) => {
    if (downloading || !selectedSheet) return;
    setDownloading(true);

    showToast(`Carregando "${selectedSheet.title}" - ${parte.instrumento}...`);

    try {
      const response = await fetch(`${API_BASE_URL}/api/download/parte/${parte.id}?action=view`, {
        headers: { 'Authorization': `Bearer ${Storage.get('authToken')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        API.trackEvent({
          tipo: getParteTrackingEventType(parte, 'view'),
          origem: 'detalhe_partitura',
          partitura_id: selectedSheet.id,
          parte_id: parte.id,
          metadata: { instrumento: parte.instrumento }
        });

        // Abre no modal embutido
        setPdfViewer({
          isOpen: true,
          url: blobUrl,
          title: selectedSheet.title,
          instrument: parte.instrumento
        });
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.error || 'Erro ao abrir arquivo', 'error');
      }
    } catch (e) {
      console.error('Erro ao visualizar:', e);
      showToast('Erro ao abrir arquivo', 'error');
    }

    setDownloading(false);
  }, [downloading, selectedSheet, showToast]);

  /**
   * Fecha o visualizador de PDF embutido
   */
  const closePdfViewer = useCallback(() => {
    // Revogar URL do blob para liberar memoria
    if (pdfViewer.url) {
      URL.revokeObjectURL(pdfViewer.url);
    }
    setPdfViewer({
      isOpen: false,
      url: null,
      title: '',
      instrument: ''
    });
  }, [pdfViewer.url]);

  /**
   * Inicia fluxo de visualização para um instrumento
   */
  const handleViewInstrument = useCallback((instrument) => {
    // Tenta correspondencia exata primeiro (resolve bug Clarinete Bb 1 vs 2)
    const exata = findParteExata(instrument, partes);
    if (exata) {
      viewParte(exata);
      return;
    }

    const correspondentes = findPartesCorrespondentes(instrument, partes);

    if (correspondentes.length === 0) {
      showToast('Parte não encontrada', 'error');
    } else {
      viewParte(correspondentes[0]);
    }
  }, [partes, viewParte, showToast]);

  /**
   * Verifica se o navegador suporta compartilhamento de arquivos
   */
  const canShareFiles = useCallback(() => {
    if (!navigator.share || !navigator.canShare) return false;
    // Testa se pode compartilhar arquivos PDF
    const testFile = new File([''], 'test.pdf', { type: 'application/pdf' });
    return navigator.canShare({ files: [testFile] });
  }, []);

  /**
   * Compartilha uma parte especifica via Web Share API
   */
  const shareParte = useCallback(async (parte) => {
    if (downloading || !selectedSheet) return;
    setDownloading(true);

    showToast(`Preparando "${selectedSheet.title}" - ${parte.instrumento}...`);

    try {
      const response = await fetch(`${API_BASE_URL}/api/download/parte/${parte.id}`, {
        headers: { 'Authorization': `Bearer ${Storage.get('authToken')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const filename = `${selectedSheet.title} - ${parte.instrumento}.pdf`;
        const file = new File([blob], filename, { type: 'application/pdf' });

        // Verifica se pode compartilhar arquivos
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `${selectedSheet.title} - ${parte.instrumento}`,
            text: `Partitura: ${selectedSheet.title}\nParte: ${parte.instrumento}`
          });
          showToast('Compartilhado com sucesso!');
        } else {
          // Fallback: baixa o arquivo se não suportar compartilhamento
          showToast('Compartilhamento não suportado. Baixando arquivo...', 'error');
          const pdfBlob = new Blob([blob], { type: 'application/pdf' });
          saveBlob(pdfBlob, filename);
        }
      } else {
        const error = await response.json().catch(() => ({}));
        showToast(error.error || 'Erro ao preparar arquivo', 'error');
      }
    } catch (e) {
      // AbortError acontece quando usuario cancela o share
      if (e.name !== 'AbortError') {
        console.error('Erro no compartilhamento:', e);
        showToast('Erro ao compartilhar arquivo', 'error');
      }
    }

    setDownloading(false);
  }, [downloading, selectedSheet, showToast]);

  /**
   * Inicia fluxo de compartilhamento para um instrumento
   */
  const handleShareInstrument = useCallback((instrument) => {
    // Tenta correspondencia exata primeiro (resolve bug Clarinete Bb 1 vs 2)
    const exata = findParteExata(instrument, partes);
    if (exata) {
      shareParte(exata);
      return;
    }

    const correspondentes = findPartesCorrespondentes(instrument, partes);

    if (correspondentes.length === 0) {
      showToast('Parte não encontrada para compartilhar', 'error');
    } else {
      shareParte(correspondentes[0]);
    }
  }, [partes, shareParte, showToast]);

  return {
    // State
    downloading,
    confirmInstrument,
    selectedParte,
    showPartePicker,
    partesDisponiveis,
    pdfViewer,

    // Actions
    downloadParteDireta,
    handleSelectInstrument,
    handleSelectParteEspecifica,
    handleConfirmDownload,
    handleCancelDownload,
    closePartePicker,
    printParte,
    handlePrintInstrument,
    viewParte,
    handleViewInstrument,
    closePdfViewer,
    shareParte,
    handleShareInstrument,
    canShareFiles,

    // Utilities
    findPartesCorrespondentes: (inst) => findPartesCorrespondentes(inst, partes),
    findParteExata: (inst) => findParteExata(inst, partes)
  };
};

export default useSheetDownload;
